import React, { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './DamageMap.css';
import { fetchInfrastructureData } from '../services/osmService';
import { generateValidatedDamagePoints, getHurricaneTrack, isPointOnLand } from '../services/damageValidationService';

// Fix for default marker icons in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Geographic center of Jamaica (for neutral island-wide view)
const JAMAICA_CENTER = [18.1096, -77.2975];

// Center of Hurricane Melissa's primary damage zone (western parishes)
const DAMAGE_CENTER = [18.15, -77.85];

const PARISHES = {
  // Severely damaged western parishes (Hurricane path - DIRECT EYEWALL IMPACT)
  'St. Elizabeth': {
    centerLat: 18.05, centerLon: -77.73,
    bounds: { minLat: 17.88, maxLat: 18.20, minLon: -78.00, maxLon: -77.45 },
    ellipse: { widthFactor: 0.25, heightFactor: 0.15 },
    count: 350, // INCREASED - direct landfall zone
    severityWeights: { destroyed: 0.30, major: 0.30, moderate: 0.25, minor: 0.10, none: 0.05 }
  },
  'Westmoreland': {
    centerLat: 18.24, centerLon: -78.14,
    bounds: { minLat: 18.18, maxLat: 18.35, minLon: -78.35, maxLon: -77.90 },
    ellipse: { widthFactor: 0.20, heightFactor: 0.08 },
    count: 300, // INCREASED - eyewall passage
    severityWeights: { destroyed: 0.25, major: 0.30, moderate: 0.25, minor: 0.15, none: 0.05 }
  },
  'Hanover': {
    centerLat: 18.41, centerLon: -78.13,
    bounds: { minLat: 18.35, maxLat: 18.47, minLon: -78.35, maxLon: -77.90 },
    ellipse: { widthFactor: 0.18, heightFactor: 0.06 },
    count: 200, // INCREASED - near eyewall
    severityWeights: { destroyed: 0.15, major: 0.25, moderate: 0.30, minor: 0.20, none: 0.10 }
  },
  'Manchester': {
    centerLat: 18.04, centerLon: -77.51,
    bounds: { minLat: 17.90, maxLat: 18.18, minLon: -77.68, maxLon: -77.35 },
    ellipse: { widthFactor: 0.15, heightFactor: 0.13 },
    count: 180, // INCREASED
    severityWeights: { destroyed: 0.08, major: 0.15, moderate: 0.30, minor: 0.30, none: 0.17 }
  },
  // Additional parishes with lighter damage
  'St. James': {
    centerLat: 18.39, centerLon: -77.90,
    bounds: { minLat: 18.25, maxLat: 18.53, minLon: -78.05, maxLon: -77.75 },
    ellipse: { widthFactor: 0.14, heightFactor: 0.13 },
    count: 85,
    severityWeights: { destroyed: 0.12, major: 0.18, moderate: 0.30, minor: 0.25, none: 0.15 }
  },
  'Trelawny': {
    centerLat: 18.35, centerLon: -77.65,
    bounds: { minLat: 18.25, maxLat: 18.45, minLon: -77.85, maxLon: -77.45 },
    ellipse: { widthFactor: 0.18, heightFactor: 0.09 },
    count: 75,
    severityWeights: { destroyed: 0.05, major: 0.12, moderate: 0.28, minor: 0.35, none: 0.20 }
  },
  'Clarendon': {
    centerLat: 17.90, centerLon: -77.35,
    bounds: { minLat: 17.75, maxLat: 18.05, minLon: -77.65, maxLon: -77.05 },
    ellipse: { widthFactor: 0.28, heightFactor: 0.14 },
    count: 95,
    severityWeights: { destroyed: 0.06, major: 0.14, moderate: 0.30, minor: 0.32, none: 0.18 }
  },
  'St. Ann': {
    centerLat: 18.34, centerLon: -77.25,
    bounds: { minLat: 18.15, maxLat: 18.53, minLon: -77.45, maxLon: -77.05 },
    ellipse: { widthFactor: 0.18, heightFactor: 0.18 },
    count: 80,
    severityWeights: { destroyed: 0.03, major: 0.08, moderate: 0.25, minor: 0.40, none: 0.24 }
  },
  'St. Catherine': {
    centerLat: 18.00, centerLon: -77.00,
    bounds: { minLat: 17.85, maxLat: 18.15, minLon: -77.25, maxLon: -76.75 },
    ellipse: { widthFactor: 0.23, heightFactor: 0.14 },
    count: 110,
    severityWeights: { destroyed: 0.04, major: 0.10, moderate: 0.28, minor: 0.38, none: 0.20 }
  },
  'St. Mary': {
    centerLat: 18.34, centerLon: -76.95,
    bounds: { minLat: 18.15, maxLat: 18.53, minLon: -77.15, maxLon: -76.75 },
    ellipse: { widthFactor: 0.18, heightFactor: 0.18 },
    count: 70,
    severityWeights: { destroyed: 0.02, major: 0.06, moderate: 0.22, minor: 0.42, none: 0.28 }
  },
  'Kingston': {
    centerLat: 17.97, centerLon: -76.80,
    bounds: { minLat: 17.92, maxLat: 18.02, minLon: -76.85, maxLon: -76.75 },
    ellipse: { widthFactor: 0.05, heightFactor: 0.05 },
    count: 120,
    severityWeights: { destroyed: 0.02, major: 0.08, moderate: 0.25, minor: 0.40, none: 0.25 }
  },
  'St. Andrew': {
    centerLat: 18.05, centerLon: -76.75,
    bounds: { minLat: 17.98, maxLat: 18.12, minLon: -76.85, maxLon: -76.65 },
    ellipse: { widthFactor: 0.09, heightFactor: 0.07 },
    count: 100,
    severityWeights: { destroyed: 0.02, major: 0.07, moderate: 0.23, minor: 0.42, none: 0.26 }
  },
  'Portland': {
    centerLat: 18.30, centerLon: -76.52,
    bounds: { minLat: 18.08, maxLat: 18.53, minLon: -76.85, maxLon: -76.18 },
    ellipse: { widthFactor: 0.30, heightFactor: 0.21 },
    count: 65,
    severityWeights: { destroyed: 0.01, major: 0.04, moderate: 0.18, minor: 0.45, none: 0.32 }
  },
  'St. Thomas': {
    centerLat: 17.88, centerLon: -76.35,
    bounds: { minLat: 17.68, maxLat: 18.08, minLon: -76.52, maxLon: -76.18 },
    ellipse: { widthFactor: 0.15, heightFactor: 0.19 },
    count: 60,
    severityWeights: { destroyed: 0.01, major: 0.05, moderate: 0.20, minor: 0.44, none: 0.30 }
  }
};

const DamageMap = () => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [currentLayer, setCurrentLayer] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [infrastructureData, setInfrastructureData] = useState([]);
  const [dataSource, setDataSource] = useState('simulated'); // 'simulated', 'osm', or 'comprehensive'
  const [error, setError] = useState(null);
  const [activeButton, setActiveButton] = useState('all'); // Single active button: 'all', 'wind', 'flood', 'ai-data', 'osm-data'

  // Update map markers with new data
  const updateMapMarkers = useCallback((data) => {
    if (!mapInstanceRef.current) return;
    
    // Clear existing markers
    markersRef.current.forEach(marker => {
      mapInstanceRef.current.removeLayer(marker);
    });
    markersRef.current = [];
    
    // Add new markers
    const markers = (dataSource === 'osm') ? addOSMMarkers(mapInstanceRef.current, data) : addMarkers(mapInstanceRef.current, data);
    markersRef.current = markers;
  }, [dataSource]);

  // Load infrastructure data
  const loadInfrastructureData = useCallback(async (source = 'simulated') => {
    setIsLoading(true);
    setError(null);
    
    try {
      let data;
      if (source === 'osm') {
        console.log('Fetching real infrastructure data from OpenStreetMap...');
        data = await fetchInfrastructureData('all');
        console.log(`Loaded ${data.length} infrastructure points from OSM`);
      } else {
        // Use ML-validated simulated data
        console.log('Generating physics-validated damage points...');
        data = generateDamagePoints();
        console.log(`Generated ${data.length} validated damage points`);
      }
      
      setInfrastructureData(data);
      setDataSource(source);
      
      // Reset layer filter to 'all' when switching data sources
      setCurrentLayer('all');
      
      // Update map markers if map exists
      if (mapInstanceRef.current) {
        updateMapMarkers(data);
      }
      
    } catch (error) {
      console.error('Error loading infrastructure data:', error);
      setError(error.message);
      // Fallback to simulated data
      if (source === 'osm') {
        console.log('Falling back to simulated data...');
        await loadInfrastructureData('simulated');
      }
    } finally {
      setIsLoading(false);
    }
  }, [updateMapMarkers]); // Remove dataSource - it's unnecessary since source is passed as parameter

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    console.log('Initializing map...');
    
    try {
      // Create map - center on damage zone to show impact immediately
      const map = L.map(mapRef.current, {
        center: DAMAGE_CENTER,
        zoom: 9,
        minZoom: 8,
        maxZoom: 18,
        zoomControl: true
      });

      // Add tile layer
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors | Real infrastructure data',
        maxZoom: 19
      }).addTo(map);

      // Add boundary
      addJamaicaBoundary(map);
      
      // Add hurricane track visualization
      addHurricaneTrack(map);

      // Store map instance
      mapInstanceRef.current = map;
      
      console.log('Map initialized successfully');

      // Force resize after a moment
      setTimeout(() => {
        map.invalidateSize();
      }, 100);

      // Load initial data (simulated)
      loadInfrastructureData('simulated');

    } catch (error) {
      console.error('Map initialization error:', error);
      setError(error.message);
      setIsLoading(false);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [loadInfrastructureData]);

  const handleLayerChange = (layer) => {
    setCurrentLayer(layer);
    
    if (!mapInstanceRef.current) return;

    // Show/hide markers based on filter
    markersRef.current.forEach(marker => {
      // Handle both simulated and OSM data structures
      const category = marker.facilityData?.category;
      const damageType = marker.damageData?.damageType;
      
      if (layer === 'all') {
        marker.addTo(mapInstanceRef.current);
      } else if (layer === 'wind') {
        // Show only WIND damage markers
        if (damageType === 'wind') {
          marker.addTo(mapInstanceRef.current);
        } else {
          marker.remove();
        }
      } else if (layer === 'flood') {
        // Show only FLOOD damage markers
        if (damageType === 'flood' || marker.damageData?.category === 'flood') {
          marker.addTo(mapInstanceRef.current);
        } else {
          marker.remove();
        }
      } else if (layer === 'healthcare' && dataSource === 'osm') {
        if (category === 'healthcare') {
          marker.addTo(mapInstanceRef.current);
        } else {
          marker.remove();
        }
      } else if (layer === 'infrastructure' && dataSource === 'osm') {
        if (['utilities', 'transportation', 'emergency'].includes(category)) {
          marker.addTo(mapInstanceRef.current);
        } else {
          marker.remove();
        }
      }
    });

    // Fly to center when showing flood damage for better view
    if (layer === 'flood') {
      mapInstanceRef.current.flyTo(JAMAICA_CENTER, 9);
    }
  };

  return (
    <div className="damage-map-wrapper">
      {isLoading && (
        <div className="map-loading-overlay">
          <div className="spinner"></div>
          <p>{dataSource === 'osm' ? 'Loading real infrastructure data from OpenStreetMap...' : 'Loading damage map...'}</p>
        </div>
      )}
      
      {error && (
        <div className="map-error">
          <p>⚠️ {error}</p>
          <button onClick={() => loadInfrastructureData('simulated')}>
            Use AI Data
          </button>
        </div>
      )}
      
      <div className="map-controls">
        <button 
          className={`map-btn ${activeButton === 'all' ? 'active' : ''}`}
          onClick={() => {
            setActiveButton('all');
            handleLayerChange('all');
          }}
        >
          Damage
        </button>
        <button 
          className={`map-btn ${activeButton === 'wind' ? 'active' : ''}`}
          onClick={() => {
            setActiveButton('wind');
            handleLayerChange('wind');
          }}
        >
          Wind
        </button>
        <button 
          className={`map-btn ${activeButton === 'flood' ? 'active' : ''}`}
          onClick={() => {
            setActiveButton('flood');
            handleLayerChange('flood');
          }}
        >
          Flood
        </button>
        <button 
          className={`map-btn source-btn ${activeButton === 'ai-data' ? 'active' : ''}`}
          onClick={() => {
            setActiveButton('ai-data');
            loadInfrastructureData('simulated');
          }}
          disabled={isLoading}
        >
          AI Data
        </button>
        <button 
          className={`map-btn source-btn ${activeButton === 'osm-data' ? 'active' : ''}`}
          onClick={() => {
            setActiveButton('osm-data');
            loadInfrastructureData('osm');
          }}
          disabled={isLoading}
        >
          OSM Data
        </button>
        {dataSource === 'osm' && (
          <>
            <button 
              className={`map-btn ${currentLayer === 'healthcare' ? 'active' : ''}`}
              onClick={() => handleLayerChange('healthcare')}
            >
              🏥 Healthcare
            </button>
            <button 
              className={`map-btn ${currentLayer === 'infrastructure' ? 'active' : ''}`}
              onClick={() => handleLayerChange('infrastructure')}
            >
              ⚡ Critical Infrastructure
            </button>
          </>
        )}
      </div>

      <div ref={mapRef} className="leaflet-map-container" />

      <div className="map-legend">
        <div className="legend-section">
          <h4 style={{ color: 'white' }}>🌪️ Wind Damage</h4>
          <div className="legend-item">
            <span className="legend-dot destroyed"></span>
            <span>Destroyed</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot major"></span>
            <span>Major Damage</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot moderate"></span>
            <span>Moderate Damage</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot minor"></span>
            <span>Minor Damage</span>
          </div>
        </div>
        
        {dataSource === 'simulated' && (
          <div className="legend-section" style={{ marginTop: '15px', paddingTop: '10px', borderTop: '1px solid #e5e7eb' }}>
            <h4 style={{ color: 'white' }}>💧 Flood Damage</h4>
            <div className="legend-item">
              <span className="legend-dot flood-destroyed"></span>
              <span>Catastrophic Flooding</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot flood-major"></span>
              <span>Major Flooding</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot flood-moderate"></span>
              <span>Moderate Flooding</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot flood-minor"></span>
              <span>Minor Flooding</span>
            </div>
            <div style={{ marginTop: '8px', fontSize: '0.85em', color: '#666' }}>
              <strong>Includes:</strong> Storm surge & rainfall
            </div>
          </div>
        )}
        
        {dataSource === 'osm' && (
          <div className="legend-section">
            <h4 style={{ color: 'white' }}>Infrastructure Status</h4>
            <div className="legend-item">
              <span className="legend-dot operational"></span>
              <span>Operational</span>
            </div>
            <div style={{ marginTop: '15px', paddingTop: '10px', borderTop: '1px solid #e5e7eb' }}>
              <h4 style={{ color: 'white', marginBottom: '8px' }}>Infrastructure Types</h4>
              <div className="legend-item">
                <span className="legend-icon">🏥</span>
                <span>Healthcare</span>
              </div>
              <div className="legend-item">
                <span className="legend-icon">🏫</span>
                <span>Education</span>
              </div>
              <div className="legend-item">
                <span className="legend-icon">🚨</span>
                <span>Emergency Services</span>
              </div>
              <div className="legend-item">
                <span className="legend-icon">⚡</span>
                <span>Utilities</span>
              </div>
              <div className="legend-item">
                <span className="legend-icon">🛣️</span>
                <span>Transportation</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper functions
function addMarkers(map, damageData) {
  const markers = [];
  
  // Wind damage colors (red/orange)
  const windColors = {
    'destroyed': '#DC2626',  // Red
    'major': '#EA580C',      // Dark orange
    'moderate': '#F59E0B',   // Orange
    'minor': '#EAB308',      // Yellow
    'none': '#22C55E'        // Green
  };
  
  // Flood damage colors (blue spectrum)
  const floodColors = {
    'destroyed': '#1E3A8A',  // Dark blue
    'major': '#2563EB',      // Blue
    'moderate': '#3B82F6',   // Light blue
    'minor': '#60A5FA',      // Sky blue
    'none': '#22C55E'        // Green
  };

  damageData.forEach(point => {
    // Determine if this is wind or flood damage
    const isFlood = point.damageType === 'flood' || point.category === 'flood';
    const colors = isFlood ? floodColors : windColors;
    const severity = point.severity || point.damageStatus;
    
    const marker = L.circleMarker([point.lat, point.lon], {
      radius: isFlood ? 7 : 6, // Slightly larger for flood markers
      fillColor: colors[severity] || '#9CA3AF',
      color: isFlood ? '#1E3A8A' : '#fff', // Blue border for flood, white for wind
      weight: 2,
      opacity: 1,
      fillOpacity: isFlood ? 0.7 : 0.8
    });
    
    // Different popup content for flood vs wind damage
    const popupContent = isFlood ? `
      <div style="font-family: sans-serif;">
        <strong>💧 Flood Damage</strong><br/>
        <strong>${point.parish}</strong><br/>
        Severity: ${severity}<br/>
        ${point.floodType ? `Type: ${point.floodType}<br/>` : ''}
        ${point.modelValidation?.elevation ? `Elevation: ${point.modelValidation.elevation}m<br/>` : ''}
        ${point.modelValidation?.distanceFromCoast ? `Coast: ${point.modelValidation.distanceFromCoast}km<br/>` : ''}
        <small style="color: #2563EB;">Storm surge & rainfall flooding</small>
      </div>
    ` : `
      <div style="font-family: sans-serif;">
        <strong>🌪️ Wind Damage</strong><br/>
        <strong>${point.parish}</strong><br/>
        Severity: ${severity}<br/>
        ${point.expectedWindSpeed ? `Wind: ${point.expectedWindSpeed} mph<br/>` : ''}
        ${point.confidence ? `<small>AI Confidence: ${point.confidence}%</small>` : ''}
      </div>
    `;

    marker.bindPopup(popupContent);
    marker.damageData = point;
    markers.push(marker);
    marker.addTo(map);
  });

  return markers;
}

function addOSMMarkers(map, infrastructureData) {
  const markers = [];
  
  // Color coding for different damage severities
  const severityColors = {
    'destroyed': '#DC2626',
    'major': '#EA580C',
    'moderate': '#F59E0B',
    'minor': '#EAB308',
    'operational': '#22C55E'
  };
  
  // Icons for different infrastructure categories
  const categoryIcons = {
    healthcare: '🏥',
    education: '🏫', 
    emergency: '🚨',
    utilities: '⚡',
    transportation: '🛣️',
    other: '📍'
  };

  infrastructureData.forEach(facility => {
    const { lat, lon, name, category, type, damageStatus } = facility;
    
    // Skip if not on land
    if (!isPointOnLand(lat, lon)) {
      return;
    }
    
    const severity = damageStatus.severity;
    
    // Create custom icon based on category and damage status
    const iconHtml = `
      <div style="
        background: ${severityColors[severity] || '#9CA3AF'};
        border: 2px solid white;
        border-radius: 50%;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        ${severity === 'destroyed' ? 'opacity: 0.7;' : ''}
      ">
        ${categoryIcons[category] || '📍'}
      </div>
    `;
    
    const customIcon = L.divIcon({
      html: iconHtml,
      className: 'osm-marker',
      iconSize: [30, 30],
      iconAnchor: [15, 15],
      popupAnchor: [0, -15]
    });

    const marker = L.marker([lat, lon], { icon: customIcon });

    // Create detailed popup with real OSM data
    const assessmentDate = new Date(damageStatus.assessmentDate).toLocaleDateString();
    const lastUpdated = new Date(facility.lastUpdated).toLocaleDateString();
    
    marker.bindPopup(`
      <div style="font-family: sans-serif; min-width: 250px;">
        <div style="border-bottom: 2px solid ${severityColors[severity]}; padding-bottom: 8px; margin-bottom: 8px;">
          <strong style="font-size: 16px;">${name}</strong><br/>
          <span style="color: #666; font-size: 12px;">${category.toUpperCase()} • ${type.replace('_', ' ').toUpperCase()}</span>
        </div>
        
        <div style="margin-bottom: 8px;">
          <strong>Damage Status:</strong> 
          <span style="color: ${severityColors[severity]}; font-weight: bold; text-transform: capitalize;">
            ${severity}
          </span>
        </div>
        
        <div style="margin-bottom: 8px;">
          <strong>Assessment:</strong> ${damageStatus.assessed ? '✅ Assessed' : '⏳ Pending'}
          ${damageStatus.assessed ? `<br/><small>Date: ${assessmentDate}</small>` : ''}
        </div>
        
        <div style="margin-bottom: 8px; padding: 6px; background: #f5f5f5; border-radius: 4px; font-size: 12px;">
          ${damageStatus.notes}
        </div>
        
        <div style="text-align: right; font-size: 10px; color: #888;">
          Data: OpenStreetMap<br/>
          Updated: ${lastUpdated}
        </div>
      </div>
    `);

    // Store facility data on marker for filtering
    marker.facilityData = facility;
    markers.push(marker);
    marker.addTo(map);
  });

  return markers;
}

function addJamaicaBoundary(map) {
  const outline = [
    [18.52, -78.37], [18.50, -78.20], [18.46, -77.90],
    [18.40, -77.45], [18.33, -77.15], [18.15, -76.72],
    [18.00, -76.50], [17.88, -76.35], [17.87, -76.18],
    [17.96, -76.35], [18.14, -76.75], [18.31, -77.25],
    [18.36, -77.68], [18.35, -78.00], [18.33, -78.37]
  ];

  L.polygon(outline, {
    color: '#009B3A',
    weight: 2,
    opacity: 0.6,
    fill: false,
    dashArray: '5, 10'
  }).addTo(map);
}

// Add hurricane track to map
function addHurricaneTrack(map) {
  const track = getHurricaneTrack();
  
  // Create track line
  const trackCoords = track.map(point => [point.lat, point.lon]);
  
  L.polyline(trackCoords, {
    color: '#ff0000',
    weight: 4,
    opacity: 0.7,
    dashArray: '10, 10',
    lineJoin: 'round'
  }).addTo(map);
  
  // Add markers for key points on the track
  track.forEach((point, index) => {
    const datetime = new Date(point.time);
    const time = datetime.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    const date = datetime.toLocaleDateString('en-US', { 
      month: 'short',
      day: 'numeric'
    });
    
    // Determine category and color based on Saffir-Simpson scale
    const category = point.category || 0;
    let categoryLabel, iconColor;
    
    switch(category) {
      case 5: categoryLabel = 'Cat 5'; iconColor = '#8B0000'; break;
      case 4: categoryLabel = 'Cat 4'; iconColor = '#DC143C'; break;
      case 3: categoryLabel = 'Cat 3'; iconColor = '#FF4500'; break;
      case 2: categoryLabel = 'Cat 2'; iconColor = '#FF8C00'; break;
      case 1: categoryLabel = 'Cat 1'; iconColor = '#FFA500'; break;
      default: categoryLabel = 'TS'; iconColor = '#FFD700'; break;
    }
    
    // Identify landfall point (18.2, -77.9 at 18:00 on Oct 28)
    const isLandfall = point.time === '2025-10-28T18:00:00';
    const markerSize = isLandfall ? 12 : (category >= 5 ? 10 : 8); // SMALLER markers - damage points are the focus
    
    const hurricaneIcon = L.divIcon({
      className: 'hurricane-track-marker',
      html: `<div style="
        background-color: ${iconColor};
        width: ${markerSize}px;
        height: ${markerSize}px;
        border-radius: 50%;
        border: ${isLandfall ? '2px solid #000' : '1.5px solid white'};
        box-shadow: 0 0 ${isLandfall ? '6' : '3'}px rgba(0,0,0,${isLandfall ? '0.7' : '0.5'});
        opacity: 0.85;
      "></div>`,
      iconSize: [markerSize, markerSize],
      iconAnchor: [markerSize / 2, markerSize / 2]
    });
    
    const marker = L.marker([point.lat, point.lon], { icon: hurricaneIcon })
      .bindPopup(`
        <div style="min-width: 180px;">
          <strong style="font-size: 1.1em;">🌀 Hurricane Melissa</strong><br/>
          <hr style="margin: 5px 0; border-color: ${iconColor};"/>
          <strong>Time:</strong> ${time}, ${date}<br/>
          <strong>Category:</strong> <span style="color: ${iconColor}; font-weight: bold;">${categoryLabel}</span><br/>
          <strong>Wind Speed:</strong> ${point.windSpeed} mph<br/>
          ${point.pressure ? `<strong>Pressure:</strong> ${point.pressure} mb<br/>` : ''}
          <strong>Position:</strong> ${point.lat.toFixed(1)}°N, ${Math.abs(point.lon).toFixed(1)}°W<br/>
          ${isLandfall ? '<div style="margin-top: 5px; padding: 5px; background: #ffe6e6; border-left: 3px solid red; font-weight: bold;">⚠️ LANDFALL - Western Jamaica</div>' : ''}
        </div>
      `)
      .addTo(map);
    
    // Auto-open popup for landfall point
    if (isLandfall) {
      setTimeout(() => marker.openPopup(), 1500);
    }
  });
  
  // Add legend entry for hurricane track
  const legend = document.querySelector('.map-legend');
  if (legend) {
    const trackLegend = document.createElement('div');
    trackLegend.className = 'legend-item';
    trackLegend.style.marginTop = '10px';
    trackLegend.style.paddingTop = '10px';
    trackLegend.style.borderTop = '1px solid #ddd';
    trackLegend.innerHTML = `
      <strong style="display: block; margin-bottom: 5px;">🌀 Hurricane Track</strong>
      <span style="display: inline-block; width: 30px; height: 3px; background: #ff0000; margin-right: 5px;"></span>
      <span style="font-size: 0.85em;">Melissa's Path</span>
    `;
    legend.appendChild(trackLegend);
  }
}

function generateDamagePoints() {
  const features = [];
  
  // Generate validated damage points for each parish using wind field model
  Object.entries(PARISHES).forEach(([name, parish]) => {
    const validatedPoints = generateValidatedDamagePoints(name, parish.bounds, parish.count);
    
    // Convert to map format and apply final land check
    validatedPoints.forEach(point => {
      // Double-check point is on land using the boundary polygon
      if (isPointOnLand(point.lat, point.lon)) {
        features.push({
          lat: point.lat,
          lon: point.lon,
          severity: point.damageStatus,
          damageStatus: point.damageStatus, // Also include as damageStatus for consistency
          damageType: point.damageType, // CRITICAL: Pass through damageType (wind/flood)
          floodType: point.floodType, // Pass through flood type
          category: point.category, // Pass through category (infrastructure/flood)
          parish: name,
          confidence: Math.round(point.modelValidation.confidenceScore * 100),
          type: point.type,
          expectedWindSpeed: point.modelValidation.expectedWindSpeed,
          distanceFromTrack: point.modelValidation.distanceFromTrack,
          modelValidation: point.modelValidation
        });
      }
    });
  });
  
  // Log summary of wind vs flood damage points
  const floodFeatures = features.filter(f => f.damageType === 'flood');
  const windFeatures = features.filter(f => f.damageType === 'wind');
  console.log(`✅ TOTAL DAMAGE POINTS: ${features.length} (${windFeatures.length} wind 🌪️, ${floodFeatures.length} flood 💧)`);
  console.log(`Physics validation: ${features.length} points generated with wind field model (all on land)`);
  
  return features;
}

export default DamageMap;
